import React from 'react';
import Layout from '../components/layout/Layout';
import { Mail, Phone, MapPin, Clock, MessageSquare } from 'lucide-react';

const ContactPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-24 md:pb-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-white/10 text-blue-300 text-sm font-semibold rounded-full mb-4 md:mb-6 backdrop-blur-sm border border-white/20">
            Get in Touch
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Contact Us
          </h1>
          <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto pb-4 md:pb-8 px-4">
            Have questions? We&apos;d love to hear from you. Reach out to us 
            and we&apos;ll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Info Cards - 4 items evenly distributed */}
      <section className="py-12 md:py-16 bg-white -mt-12 md:-mt-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 -mt-16 md:-mt-24">
            {/* Call Us */}
            <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-100 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6">
                <Phone className="w-6 h-6 md:w-7 md:h-7 text-blue-600" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-600 mb-3 md:mb-4 text-sm md:text-base">Mon-Sat from 9am to 6pm</p>
              <a href="tel:+917996697777" className="text-blue-600 font-medium hover:text-blue-700 transition-colors text-sm md:text-base">
                +91 79966 97777
              </a>
            </div>

            {/* Email Us */}
            <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-teal-100 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6">
                <Mail className="w-6 h-6 md:w-7 md:h-7 text-teal-600" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Email Us</h3>
              <p className="text-gray-600 mb-3 md:mb-4 text-sm md:text-base">We&apos;ll respond within 24 hours</p>
              <a href="mailto:info@steamie.net" className="text-blue-600 font-medium hover:text-blue-700 transition-colors text-sm md:text-base break-all">
                info@steamie.net
              </a>
            </div>

            {/* Visit Us */}
            <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-orange-100 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6">
                <MapPin className="w-6 h-6 md:w-7 md:h-7 text-orange-600" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Visit Us</h3>
              <p className="text-gray-600 mb-3 md:mb-4 text-sm md:text-base">Come say hello at our office</p>
              <span className="text-blue-600 font-medium text-sm md:text-base">
                #101,102 ARG Shopping Complex,<br />
                Jaipur Road, Ajmer,<br />
                Rajasthan 305001
              </span>
            </div>

            {/* Office Hours */}
            <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-purple-100 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6">
                <Clock className="w-6 h-6 md:w-7 md:h-7 text-purple-600" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Office Hours</h3>
              <p className="text-gray-600 mb-3 md:mb-4 text-sm md:text-base">When we&apos;re available</p>
              <div className="text-blue-600 font-medium text-sm md:text-base">
                <p>Mon-Fri: 9:00 AM - 6:00 PM</p>
                <p>Sat: 10:00 AM - 4:00 PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 text-sm md:text-base">Find answers to common questions about STEAM-IE</p>
          </div>
          
          <div className="space-y-4 md:space-y-6">
            {[
              {
                q: 'How do I get started with STEAM-IE?',
                a: 'Simply give us a call or send an email. Our team will guide you through our programs and help you choose the right option.'
              },
              {
                q: 'What age groups do you cater to?',
                a: 'We offer programs for K-12 students (ages 5-18), as well as specialized training for educators and professionals.'
              },
              {
                q: 'Do you offer school partnerships?',
                a: 'Yes! We provide comprehensive solutions for schools including curriculum integration, teacher training, and lab setup.'
              },
              {
                q: 'Are your certifications recognized?',
                a: 'STEAM-IE is India\'s first credentialing company for STEAM education. Our certifications are recognized nationwide.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg md:rounded-xl p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-start text-sm md:text-base">
                  <MessageSquare className="w-4 h-4 md:w-5 md:h-5 text-blue-600 mr-2 md:mr-3 mt-0.5 flex-shrink-0" />
                  {item.q}
                </h4>
                <p className="text-gray-600 ml-6 md:ml-8 text-sm md:text-base">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
